import { ACTIVE_INFLUENCERS_TAB_DATA, TAB_DATA } from '../common/metadata';
import {
  AI_CONVO_GET_COMPANY,
  AI_CONVO_History,
  AI_CONVO_History_SHOW,
  AI_CONVO_USER_History,
  AI_CONVO_USER_History_By_Chart,
  ALERTS_COMPANY_LIST,
  ALERTS_SOCIALMEDIA_LIST,
  CHART_COMBINED_SUMMARY_URI,
  CHART_FRESH_RESULTS_URI,
  CHART_ROUTER_MODEL_CACHED_URI,
  COMPANY_ALERTS_DATE_URI_BY_TYPE,
  FAVORITES_COMPANY_LIST,
  GENAICOMPANY_ADD,
  GENAICOMPANY_DELETE,
  GENAICOMPANY_EDIT,
  GENAICOMPANY_GET,
  PROFILE_INFO_URI_BY_TYPE,
} from '../services/common/endpoints';
import { get, post } from '../services/common/axiosInstance';

import { TYPE } from '../common/constants';
import { useCallback } from 'react';
import useReactQueryCustomHook from '../modules/customHooks/useStateSearchQuery.customHook';

/**
 *
 * @param {Object} body
 * @returns
 */
// Fixed platform order for the chart-agent "context" object below. The
// backend matches saved/cached chart answers on this context exactly, so
// sending the same platforms in a different array order would look like a
// different request and silently miss the cache — always build the array
// through this order, never pass the raw selection order through.
export const CHART_CONTEXT_PLATFORM_ORDER = [
  'facebook',
  'instagram',
  'twitter',
  'tiktok',
  'youtube',
  'linkedin',
  'linkedinPublicPage',
  'reddit',
  'articles',
  'trustPilotcompanyName',
  'facebookAds',
];

/**
 * Builds the one chart-agent "context" object the backend uses to match a
 * chart's saved/cached answer: { handlerUsed, socialMediaType, socialMediaSubType?,
 * day_count | (start_date + end_date), interval }. Every call that asks a
 * chart's AI agent a question, or saves its chat afterwards, must build this
 * the same way — matching is exact, so ad-hoc construction per call site
 * (the previous pattern) silently breaks reuse.
 *
 * `searchObject` is this app's existing dashboard filter object (from
 * `getSearchObject()` / `Transformer.Insights.js`): it carries `interval`
 * and either `days` or `startDate`+`endDate`, under those camelCase names —
 * this helper is what translates them into the snake_case field names the
 * backend's chart-agent endpoints expect.
 */
export function buildChartContext({
  handlerUsed,
  socialMediaType,
  socialMediaSubType,
  searchObject,
}) {
  const platforms = Array.isArray(socialMediaType)
    ? socialMediaType
    : [socialMediaType];
  const orderedKnown = CHART_CONTEXT_PLATFORM_ORDER.filter((p) =>
    platforms.includes(p),
  );
  const unknownSorted = platforms
    .filter((p) => p && !CHART_CONTEXT_PLATFORM_ORDER.includes(p))
    .sort();

  const context = {
    handlerUsed: (handlerUsed || '').toLowerCase(),
    socialMediaType: [...orderedKnown, ...unknownSorted],
    interval: searchObject?.interval,
  };

  if (socialMediaSubType) {
    context.socialMediaSubType = socialMediaSubType;
  }

  if (searchObject?.startDate && searchObject?.endDate) {
    context.start_date = searchObject.startDate;
    context.end_date = searchObject.endDate;
  } else {
    context.day_count = Number(searchObject?.days);
  }

  return context;
}

/**
 * Checks whether a chart already has a saved answer (same company, platforms,
 * sub-type, period and interval, saved in the last 24h, not marked failed)
 * before paying to run its agent again. Returns the saved {summary, date,
 * conversationId, source}, or null if the chart is stale / the check itself
 * failed - either way, the caller's existing "run the chart" path is the
 * correct fallback (never block a chart on this lookup).
 *
 * `chartName` must be the exact chart name the app already saves chats under
 * (the `socialMediaPillsName` sent to /api/livesearch/gpt) - the backend
 * matching is exact, not fuzzy.
 */
export async function getFreshChartAnswer({ chartName, context }) {
  if (!chartName || !context?.handlerUsed) return null;
  // Period is required by the endpoint; without it there's nothing to match on.
  const hasPeriod = context.day_count || (context.start_date && context.end_date);
  if (!hasPeriod) return null;
  try {
    const response = await post(CHART_FRESH_RESULTS_URI, {
      data: { pills: [chartName], ...context },
    });
    return response?.data?.fresh?.[chartName] || null;
  } catch (error) {
    // 400/401/500 or a network error - fall back to running the chart as today.
    return null;
  }
}

/**
 * Batch form of getFreshChartAnswer: checks up to 20 chart names in one
 * call, for the Summarise feature (T6/T7) - it needs to know ahead of time
 * which of the user's selected charts already have an answer and which
 * must actually run, rather than checking one chart at a time.
 *
 * Returns the raw { fresh: {chartName: {conversationId, date, summary,
 * source}}, stale: [chartName, ...] } shape, or { fresh: {}, stale: pills }
 * (treat everything as stale) if the call itself fails - Summarise should
 * never be blocked by this check.
 */
export async function getFreshChartAnswers({ pills, context }) {
  const safePills = Array.isArray(pills) ? pills.filter(Boolean) : [];
  if (!safePills.length || !context?.handlerUsed) {
    return { fresh: {}, stale: safePills };
  }
  const hasPeriod = context.day_count || (context.start_date && context.end_date);
  if (!hasPeriod) return { fresh: {}, stale: safePills };
  try {
    const response = await post(CHART_FRESH_RESULTS_URI, {
      data: { pills: safePills, ...context },
    });
    return {
      fresh: response?.data?.fresh || {},
      stale: response?.data?.stale || safePills,
    };
  } catch (error) {
    return { fresh: {}, stale: safePills };
  }
}

/**
 * Runs one stale chart's agent via the cached router endpoint (same
 * request/response contract as /api/router_model/v5, plus caching - see
 * buildChartContext's docs) and saves the chat exactly as every other
 * chart-agent save in this app does, plus the runId that ties it to this
 * Summarise click (section 7 of the spec).
 *
 * `prompt` + `content` are the exact values the chart's own SearchSummary
 * instance already built for its own auto-fire call (captured via the
 * onChartSummaryReady registration), so re-running it here produces the
 * identical request a normal chart-agent run would.
 */
export async function runChartAgentForSummarise({
  chart,
  prompt,
  content,
  context,
  runId,
}) {
  const response = await post(CHART_ROUTER_MODEL_CACHED_URI, {
    data: {
      prompt,
      content,
      socialMediaPillsName: chart,
      ...context,
    },
  });
  const data = response?.data;
  const answer = data?.choices?.[0]?.message?.content;
  if (!answer) {
    throw new Error('router_model_cached returned no answer');
  }

  await ConvoHistory({
    conversationId: data?.id,
    chatId: data?.id,
    createdTime: data?.created || Math.floor(Date.now() / 1000),
    messages: [
      { role: 'user', content: prompt },
      { role: 'assistant', content: answer },
    ],
    usage: data?.usage,
    chatName: chart,
    socialMediaPillsName: chart,
    source: 'router_model',
    sourceAPI: 'router_model',
    ...context,
    runId,
    agentStatus: 'ok',
  }).catch(() => {});

  return { chart, answer, conversationId: data?.id, cached: !!data?.cached };
}

/**
 * Writes the final Combined Summary from the selected charts' saved
 * answers (section 6/8 of the spec) - the backend reads whichever chat
 * each pill's `reuse` conversationId (or, failing that, its newest saved
 * chat) points to; it never re-runs a chart itself.
 */
export async function getCombinedSummary({ runId, pills, context, reuse }) {
  const response = await post(CHART_COMBINED_SUMMARY_URI, {
    data: {
      runId,
      pills,
      ...context,
      reuse: reuse || {},
    },
  });
  return response?.data;
}

export async function ConvoHistory(body) {
  if (typeof body !== 'object' || !body)
    return { message: 'request body is required.' };

  const response = await post(AI_CONVO_History, {
    data: body,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  return response;
}

export async function AddCompanyGenai(body) {
  if (typeof body !== 'object' || !body)
    return { message: 'request body is required.' };

  const response = await post(GENAICOMPANY_ADD, {
    data: body,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  return response;
}

export async function EditCompanyGenai(body) {
  if (typeof body !== 'object' || !body || !body.id) {
    return { message: 'Request body with company id is required.' };
  }

  const { id, companyname, ...rest } = body;

  const url = `${GENAICOMPANY_EDIT}?id=${id}`;

  const response = await post(url, {
    data: {
      companyname,
      ...rest,
    },
    headers: {
      'Content-Type': 'application/json',
    },
  });

  return response;
}
export async function DeleteCompanyGenai(source) {
  const response = await get(`${GENAICOMPANY_DELETE}&id=${source?.id}`, {});

  return response;
}
// export async function DeleteCompanyGenai(body) {
//   if (typeof body !== 'object' || !body || !body.id) {
//     return { message: 'Request body with company id is required.' };
//   }

//   const { id } = body;

//   const url = `${GENAICOMPANY_DELETE}?id=${id}`;

//   const response = await post(url, {
//     data: { id },
//     headers: {
//       'Content-Type': 'application/json',
//     },
//   });

//   return response;
// }

export async function ConvoHistoryDisplay(body) {
  if (typeof body !== 'object' || !body)
    return { message: 'request body is required.' };

  const response = await get(AI_CONVO_History_SHOW, {
    params: body,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  return response;
}

export async function ConvoHistoryDisplayByChart(body) {
  if (typeof body !== 'object' || !body)
    return { message: 'request body is required.' };

  const response = await get(AI_CONVO_USER_History_By_Chart, {
    params: body,
    headers: {
      'Content-Type': 'application/json',
    },
  });
  return response;
}

export async function UserHistory(body) {
  if (typeof body !== 'object' || !body)
    return { message: 'request body is required.' };

  const response = await get(AI_CONVO_USER_History, {
    params: body,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  return response;
}
export async function GetCompanyGenai() {
  const response = await get(`${GENAICOMPANY_GET}`, {});

  return response;
}

export async function getCompany(source) {
  const response = await get(`${AI_CONVO_GET_COMPANY}&source=${source}`, {});

  return response;
}

export async function getCompanyListFavorite() {
  const response = await get(`${FAVORITES_COMPANY_LIST}`, {});

  return response;
}

export async function getProfileByType(source) {
  const response = await get(
    `${PROFILE_INFO_URI_BY_TYPE}&field_to_show=${source}`,
    {},
  );

  return response;
}

async function getProfileByTypeAlerts(source) {
  const response = await get(
    `${PROFILE_INFO_URI_BY_TYPE}&field_to_show=${source}`,
    {},
  );

  return response;
}

// export function usePlatformDataAlerts(source) {
//   const data = useReactQueryCustomHook(['alerts'], () =>
//     getProfileByTypeAlerts(source),
//   );

//   return data;
// }

export function usePlatformDataAlerts(source) {
  const fetchAlerts = useCallback(
    () => getProfileByTypeAlerts(source),
    [source],
  );

  return useReactQueryCustomHook(
    ['alerts', 'favourites'],
    fetchAlerts, // Memoized function
    {
      staleTime: 1000 * 60 * 60 * 6,
      cacheTime: 1000 * 60 * 10,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    },
  );
}

export async function getAvailableDateByCompany(source) {
  const response = await get(
    `${COMPANY_ALERTS_DATE_URI_BY_TYPE}&companyName=${source}`,
    {},
  );

  return response;
}

export async function getCompanyListAlerts() {
  const response = await get(`${ALERTS_COMPANY_LIST}`, {});
  return response;
}

export async function getSociaMediaListAlerts() {
  const response = await get(`${ALERTS_SOCIALMEDIA_LIST}`, {});
  return response;
}

// export const CHARTS_RESPONSE = {
//   [TAB_DATA.activeFollowers]: TYPE.activefollowers,
//   [TAB_DATA.aspectSentiment]: TYPE.aspectsentiment,
//   [TAB_DATA.aspectSentimentTrend]: TYPE.aspectsentiment,
//   [TAB_DATA.followersTimeline]: 'timelinePageLikes',
//   [TAB_DATA.hashtagTrend]: TYPE.hashtags,
//   [TAB_DATA.mentionsTimeline]: TYPE.posts,
//   [TAB_DATA.sentimentByCategory]: TYPE.categorySentiment,
//   [TAB_DATA.mentionsTimelineSummary]: TYPE.,
//   [TAB_DATA.mentionsTimelineHistory]: TYPE.activefollowers,
//   [TAB_DATA.engagementEntries]: TYPE.activefollowers,
//   [TAB_DATA.sentimentTrend]: 'timeline',
//   [TAB_DATA.engagementPercentageTrend]: TYPE.activefollowers,
//   [TAB_DATA.stats]: TYPE.activefollowers,
//   [TAB_DATA.topCategories]: TYPE.activefollowers,
//   [TAB_DATA.imageAnalysisRanking]: TYPE.activefollowers,
//   [TAB_DATA.imageAnalysisTrend]: TYPE.activefollowers,
//   [TAB_DATA.topEntities]: TYPE.activefollowers,
//   [TAB_DATA.topEntitiesTrend]: TYPE.activefollowers,
//   [TAB_DATA.topHashtags]: TYPE.activefollowers,
//   [TAB_DATA.topInfluencersRanking]: TYPE.activefollowers,
//   [TAB_DATA.topFollowers]: TYPE.activefollowers,
//   [TAB_DATA.userActiveTimeline]: TYPE.activefollowers,
//   [TAB_DATA.engagementStats]: TYPE.activefollowers,
//   [TAB_DATA.userActivity]: TYPE.activefollowers,
//   [TAB_DATA.emotionInsights]: TYPE.activefollowers,
//   [TAB_DATA.followersDemographic]: TYPE.activefollowers,
//   [TAB_DATA.topInfluencers]: TYPE.activefollowers,
//   [TAB_DATA.topComments]: TYPE.activefollowers,
//   [ACTIVE_INFLUENCERS_TAB_DATA.followersReach]: TYPE,
//   [ACTIVE_INFLUENCERS_TAB_DATA.influencerStats]: TYPE,
//   [ACTIVE_INFLUENCERS_TAB_DATA.followersReach]: TYPE,
//   [ACTIVE_INFLUENCERS_TAB_DATA.followersTimeline]: TYPE,
//   [ACTIVE_INFLUENCERS_TAB_DATA.followersTrend]: TYPE,
// };
